/**
 * Shared helper functions for unit geometry creation
 */

import * as THREE from 'three';
import { COLORS } from './unitConstants';

/**
 * Create Chinese style pointed helmet (尖顶盔)
 * - Round dome with pointed spike on top
 * - Gold border and decorations
 * - Neck guard (护颈)
 */
export function createChineseHelmet(
  parts: THREE.BufferGeometry[],
  colors: THREE.Color[],
  y: number,
  options: {
    hasSpike?: boolean;
    hasTassel?: boolean;
    hasNeckGuard?: boolean;
    hasFaceMask?: boolean;
    spikeHeight?: number;
    helmetColor?: THREE.Color;
  } = {}
): void {
  const helmetColor = options.helmetColor ?? COLORS.iron;
  const hasSpike = options.hasSpike ?? true;
  const hasTassel = options.hasTassel ?? false;
  const hasNeckGuard = options.hasNeckGuard ?? true;
  const hasFaceMask = options.hasFaceMask ?? false;
  const spikeHeight = options.spikeHeight ?? 0.06;

  // Helmet dome (半球形盔体)
  const dome = new THREE.SphereGeometry(
    0.11,
    8,
    6,
    0,
    Math.PI * 2,
    0,
    Math.PI * 0.55
  );
  dome.translate(0, y, 0);
  parts.push(dome);
  colors.push(helmetColor);

  // Gold rim (金边)
  const rim = new THREE.TorusGeometry(0.105, 0.012, 4, 12);
  rim.rotateX(Math.PI / 2);
  rim.translate(0, y - 0.02, 0);
  parts.push(rim);
  colors.push(COLORS.gold);

  // Gold forehead decoration (护额)
  const foreheadPlate = new THREE.BoxGeometry(0.08, 0.03, 0.02);
  foreheadPlate.translate(0, y - 0.01, 0.09);
  parts.push(foreheadPlate);
  colors.push(COLORS.gold);

  if (hasSpike) {
    // Pointed spike (尖刺)
    const spike = new THREE.ConeGeometry(0.02, spikeHeight, 6);
    spike.translate(0, y + 0.06 + spikeHeight / 2, 0);
    parts.push(spike);
    colors.push(COLORS.gold);

    // Spike base
    const spikeBase = new THREE.CylinderGeometry(0.025, 0.02, 0.02, 6);
    spikeBase.translate(0, y + 0.06, 0);
    parts.push(spikeBase);
    colors.push(COLORS.gold);
  }

  if (hasTassel) {
    // Red tassel (红缨)
    const tasselBase = new THREE.SphereGeometry(0.015, 5, 4);
    tasselBase.translate(0, y + 0.08, 0);
    parts.push(tasselBase);
    colors.push(COLORS.tassel);

    // Tassel strands
    for (let i = 0; i < 6; i++) {
      const strand = new THREE.CylinderGeometry(0.004, 0.002, 0.05, 4);
      const angle = (i / 6) * Math.PI * 2;
      strand.rotateZ(0.4);
      strand.rotateY(angle);
      strand.translate(
        Math.cos(angle) * 0.01,
        y + 0.05,
        Math.sin(angle) * 0.01
      );
      parts.push(strand);
      colors.push(COLORS.tassel);
    }
  }

  if (hasNeckGuard) {
    // Neck guard (护颈)
    const neckGuard = new THREE.CylinderGeometry(
      0.10,
      0.12,
      0.04,
      8,
      1,
      true,
      Math.PI * 0.25,
      Math.PI * 1.5
    );
    neckGuard.translate(0, y - 0.04, 0);
    parts.push(neckGuard);
    colors.push(helmetColor);
  }

  if (hasFaceMask) {
    // Face mask with eye slit (面甲)
    const faceMask = new THREE.BoxGeometry(0.10, 0.08, 0.02);
    faceMask.translate(0, y - 0.06, 0.08);
    parts.push(faceMask);
    colors.push(helmetColor);

    // Eye slit
    const eyeSlit = new THREE.BoxGeometry(0.07, 0.015, 0.025);
    eyeSlit.translate(0, y - 0.04, 0.09);
    parts.push(eyeSlit);
    colors.push(new THREE.Color(0x111111));
  }
}

/**
 * Create Chinese style armor torso (明光甲/扎甲)
 */
export function createArmorTorso(
  parts: THREE.BufferGeometry[],
  colors: THREE.Color[],
  yBase: number,
  options: {
    width?: number;
    height?: number;
    hasShoulderPlates?: boolean;
    hasChestPlate?: boolean;
    armorSkirtLength?: number;
    primaryColor?: THREE.Color;
  } = {}
): void {
  const width = options.width ?? 0.09;
  const height = options.height ?? 0.12;
  const primaryColor = options.primaryColor ?? COLORS.markerPrimary;
  const hasShoulderPlates = options.hasShoulderPlates ?? true;
  const hasChestPlate = options.hasChestPlate ?? false;
  const armorSkirtLength = options.armorSkirtLength ?? 0.10;

  // Main torso (战袍)
  const torso = new THREE.CylinderGeometry(width * 0.9, width, height, 8);
  torso.translate(0, yBase + height / 2, 0);
  parts.push(torso);
  colors.push(primaryColor);

  // Belt (腰带)
  const belt = new THREE.CylinderGeometry(
    width + 0.01,
    width + 0.01,
    0.025,
    8
  );
  belt.translate(0, yBase + 0.01, 0);
  parts.push(belt);
  colors.push(COLORS.gold);

  // Belt buckle (带扣)
  const buckle = new THREE.BoxGeometry(0.03, 0.025, 0.015);
  buckle.translate(0, yBase + 0.01, width + 0.01);
  parts.push(buckle);
  colors.push(COLORS.gold);

  if (hasChestPlate) {
    // Chest plate (护心镜)
    const chestPlate = new THREE.CylinderGeometry(0.04, 0.04, 0.015, 8);
    chestPlate.rotateX(Math.PI / 2);
    chestPlate.translate(0, yBase + height - 0.02, width * 0.9 + 0.01);
    parts.push(chestPlate);
    colors.push(COLORS.bronze);
  }

  if (hasShoulderPlates) {
    // Shoulder plates (肩甲)
    for (const side of [-1, 1]) {
      const shoulder = new THREE.BoxGeometry(0.06, 0.025, 0.08);
      shoulder.translate(side * (width + 0.02), yBase + height - 0.02, 0);
      parts.push(shoulder);
      colors.push(COLORS.markerSecondary);

      // Shoulder edge decoration
      const edge = new THREE.BoxGeometry(0.065, 0.008, 0.085);
      edge.translate(side * (width + 0.02), yBase + height - 0.005, 0);
      parts.push(edge);
      colors.push(COLORS.gold);
    }
  }

  // Armor skirt (甲裙)
  const skirt = new THREE.CylinderGeometry(width, width * 1.3, armorSkirtLength, 8);
  skirt.translate(0, yBase - armorSkirtLength / 2 + 0.01, 0);
  parts.push(skirt);
  colors.push(primaryColor);
}

/**
 * Create an arm with upper arm, forearm, and hand
 */
export function createArm(
  parts: THREE.BufferGeometry[],
  colors: THREE.Color[],
  xOffset: number,
  yBase: number,
  armColor: THREE.Color,
  handColor: THREE.Color,
  armAngle: number = 0,
  forearmAngle: number = 0
): void {
  const armLength = 0.08;
  const forearmLength = 0.07;

  // Upper arm
  const upperArm = new THREE.CylinderGeometry(0.025, 0.022, armLength, 6);
  upperArm.rotateZ(armAngle);
  const armOffsetX = xOffset + Math.sin(armAngle) * armLength * 0.5;
  const armOffsetY = yBase - Math.cos(armAngle) * armLength * 0.5;
  upperArm.translate(armOffsetX, armOffsetY, 0);
  parts.push(upperArm);
  colors.push(armColor);

  // Forearm
  const forearm = new THREE.CylinderGeometry(0.022, 0.018, forearmLength, 6);
  const totalAngle = armAngle + forearmAngle;
  forearm.rotateZ(totalAngle);
  const forearmX =
    xOffset +
    Math.sin(armAngle) * armLength +
    Math.sin(totalAngle) * forearmLength * 0.5;
  const forearmY =
    yBase -
    Math.cos(armAngle) * armLength -
    Math.cos(totalAngle) * forearmLength * 0.5;
  forearm.translate(forearmX, forearmY, 0);
  parts.push(forearm);
  colors.push(armColor);

  // Hand
  const hand = new THREE.SphereGeometry(0.022, 5, 4);
  const handX =
    xOffset +
    Math.sin(armAngle) * armLength +
    Math.sin(totalAngle) * forearmLength;
  const handY =
    yBase -
    Math.cos(armAngle) * armLength -
    Math.cos(totalAngle) * forearmLength;
  hand.translate(handX, handY, 0);
  parts.push(hand);
  colors.push(handColor);
}

/**
 * Create a leg with thigh, shin, and foot
 */
export function createLeg(
  parts: THREE.BufferGeometry[],
  colors: THREE.Color[],
  xOffset: number,
  yBase: number,
  legColor: THREE.Color,
  footColor: THREE.Color,
  legAngle: number = 0
): void {
  const thighLength = 0.08;
  const shinLength = 0.07;

  // Thigh
  const thigh = new THREE.CylinderGeometry(0.032, 0.028, thighLength, 6);
  thigh.rotateZ(legAngle);
  const thighX = xOffset + Math.sin(legAngle) * thighLength * 0.5;
  const thighY = yBase - Math.cos(legAngle) * thighLength * 0.5;
  thigh.translate(thighX, thighY, 0);
  parts.push(thigh);
  colors.push(legColor);

  // Shin
  const shin = new THREE.CylinderGeometry(0.028, 0.024, shinLength, 6);
  shin.rotateZ(legAngle);
  const shinX = xOffset + Math.sin(legAngle) * (thighLength + shinLength * 0.5);
  const shinY = yBase - Math.cos(legAngle) * (thighLength + shinLength * 0.5);
  shin.translate(shinX, shinY, 0);
  parts.push(shin);
  colors.push(legColor);

  // Foot
  const foot = new THREE.BoxGeometry(0.035, 0.02, 0.05);
  const footX = xOffset + Math.sin(legAngle) * (thighLength + shinLength);
  const footY =
    yBase - Math.cos(legAngle) * (thighLength + shinLength) - 0.01;
  foot.translate(footX, footY, 0.01);
  parts.push(foot);
  colors.push(footColor);
}

/**
 * Merge multiple geometries with vertex colors
 */
export function mergeGeometries(
  geometries: THREE.BufferGeometry[],
  colors: THREE.Color[]
): THREE.BufferGeometry {
  // Convert indexed geometries to non-indexed
  const nonIndexedGeos: THREE.BufferGeometry[] = [];
  for (const geo of geometries) {
    nonIndexedGeos.push(geo.index ? geo.toNonIndexed() : geo);
  }

  let totalPositions = 0;
  for (const geo of nonIndexedGeos) {
    totalPositions += geo.attributes.position.array.length;
  }

  const positions = new Float32Array(totalPositions);
  const vertexColors = new Float32Array(totalPositions);
  const normals = new Float32Array(totalPositions);

  let offset = 0;
  for (let i = 0; i < nonIndexedGeos.length; i++) {
    const geo = nonIndexedGeos[i];
    const posArray = geo.attributes.position.array as Float32Array;
    const color = colors[i];

    positions.set(posArray, offset);

    // Copy normals if available
    if (geo.attributes.normal) {
      const normalArray = geo.attributes.normal.array as Float32Array;
      normals.set(normalArray, offset);
    }

    // Set vertex colors with slight variation
    for (let j = 0; j < posArray.length; j += 3) {
      const variation = 0.92 + Math.random() * 0.16;
      vertexColors[offset + j] = color.r * variation;
      vertexColors[offset + j + 1] = color.g * variation;
      vertexColors[offset + j + 2] = color.b * variation;
    }

    offset += posArray.length;
  }

  // Dispose original geometries
  for (const geo of geometries) {
    geo.dispose();
  }
  for (const geo of nonIndexedGeos) {
    if (!geometries.includes(geo)) {
      geo.dispose();
    }
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  merged.computeVertexNormals();

  return merged;
}
